import { StripeElementsOptions } from '@stripe/stripe-js';
import { useEffect, useMemo } from 'react';
import useCreatePaymentMethodSetup from '@/api/mutations/useCreatePaymentMethodSetup';
import SpinnerIcon from '@/components/icons/SpinnerIcon';
import PaymentMethodSetupForm from '@/modules/billing/components/PaymentMethodSetupForm';
import { BillingPaymentMethodListPath } from '@/modules/billing/paths';
import { stripePromise } from '@/modules/billing/stripe';
import SettingsHeader from '@/modules/settings/components/SettingsHeader';
import { CreditCardIcon } from '@heroicons/react/24/outline';
import { Elements } from '@stripe/react-stripe-js';
import { useNavigate } from 'react-router-dom';

const PaymentMethodAddUnavailable = () => {
  return (
    <>
      <SettingsHeader title="Add Payment Method" icon={<CreditCardIcon className="w-6 h-6" />} />
      <div className="rounded-md bg-red-50 p-4 text-sm text-red-700">
        Unable to start payment method setup. Please try again.
      </div>
    </>
  );
};

const PaymentMethodAddContent = () => {
  const navigate = useNavigate();

  const { createSetup, setup, isLoading, error } = useCreatePaymentMethodSetup();

  useEffect(() => {
    void createSetup();
  }, [createSetup]);

  const elementsOptions = useMemo<StripeElementsOptions | undefined>(() => {
    if (!setup?.clientSecret) return undefined;
    return { clientSecret: setup.clientSecret };
  }, [setup?.clientSecret]);

  const handleSuccess = async () => {
    navigate(BillingPaymentMethodListPath, {
      state: { paymentMethodAdded: true },
    });
  };

  if (isLoading) {
    return (
      <>
        <SettingsHeader title="Add Payment Method" icon={<CreditCardIcon className="w-6 h-6" />} />
        <div className="flex justify-center py-20">
          <SpinnerIcon />
        </div>
      </>
    );
  }

  if (error || !elementsOptions || !setup?.clientSecret) return <PaymentMethodAddUnavailable />;

  return (
    <>
      <SettingsHeader title="Add Payment Method" icon={<CreditCardIcon className="w-6 h-6" />} />

      <div className="max-w-xl">
        <p className="mb-4 text-sm text-gray-600">
          Add a payment method for subscription billing. Your card details are handled securely by
          Stripe.
        </p>

        <Elements stripe={stripePromise} options={elementsOptions}>
          <PaymentMethodSetupForm clientSecret={setup.clientSecret} onSuccess={handleSuccess} />
        </Elements>
      </div>
    </>
  );
};

const PaymentMethodAdd = () => {
  if (!stripePromise) return <PaymentMethodAddUnavailable />;
  return <PaymentMethodAddContent />;
};

export default PaymentMethodAdd;
